import type { SelectedTab } from "@v1/features/experiments/tabs/constants/tabOptions";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { DeprecatedTabs } from "@v1/components/designSystem/tabs/Tabs";
import {
  TAB_OPTION_DETAILS,
  TAB_OPTION_RESULTS,
} from "@v1/features/experiments/tabs/constants/tabOptions";
import { useExperimentDetails } from "@v1/features/experiments/tabs/hooks/useExperimentDetails";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { useSubscriptionInfo } from "@v2/hooks/subscription";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { routes } from "@v2/utils/router";
import { generatePath, Link, useLocation, useParams } from "react-router-dom";
import { getExperimentStatus } from "schemas/experiment";

export const TabMenu: React.FC = () => {
  const { experiment } = useExperimentDetails();
  const status = experiment ? getExperimentStatus(experiment) : null;
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();
  const { experimentId } = useParams();
  const logEvent = useLogAnalytics();
  const { subscriptionInfo } = useSubscriptionInfo();
  const subscriptionTier = subscriptionInfo?.tier || "free";
  const location = useLocation();
  const isResultsTab = location.pathname.includes(TAB_OPTION_RESULTS.value);
  const selectedTab = isResultsTab ? TAB_OPTION_RESULTS : TAB_OPTION_DETAILS;
  const isResultsDisabled = status === "draft";

  let detailsPath: string;
  let resultsPath: string;
  if (Boolean(projectId)) {
    detailsPath = generatePath(routes.shops.experiments.details, {
      projectId,
      experimentId,
    });
    resultsPath = generatePath(routes.shops.experiments.results, {
      projectId,
      experimentId,
    });
  } else {
    detailsPath = generatePath(routes.workspace.experiments.details, {
      workspaceId,
      experimentId,
    });
    resultsPath = generatePath(routes.workspace.experiments.results, {
      workspaceId,
      experimentId,
    });
  }

  const handleClick = (tab: SelectedTab) => {
    if (tab === TAB_OPTION_DETAILS) {
      logEvent("experiment.detail.select", {
        billingPlanTier: subscriptionTier,
      });
    } else if (tab === TAB_OPTION_RESULTS) {
      logEvent("experiment.results.select", {
        billingPlanTier: subscriptionTier,
      });
    }
  };

  const tabsList = [
    {
      ...TAB_OPTION_DETAILS,
      asChild: true,
      label: (
        <Link
          key={TAB_OPTION_DETAILS.value}
          to={detailsPath}
          onClick={() => handleClick(TAB_OPTION_DETAILS)}
        >
          {TAB_OPTION_DETAILS.label}
        </Link>
      ),
    },
    {
      ...TAB_OPTION_RESULTS,
      asChild: true,
      label: isResultsDisabled ? (
        <Tooltip content="Start test to view results" delay={300}>
          {TAB_OPTION_RESULTS.label}
        </Tooltip>
      ) : (
        <Link
          key={TAB_OPTION_RESULTS.value}
          to={resultsPath}
          onClick={() => handleClick(TAB_OPTION_RESULTS)}
        >
          {TAB_OPTION_RESULTS.label}
        </Link>
      ),
    },
  ];

  return (
    <DeprecatedTabs
      tabsOptions={tabsList}
      value={selectedTab.value}
      onValueChange={() => {}}
      defaultValue={selectedTab.value}
      size="base"
    />
  );
};
